/**
 * Modal informing users with stale MSAL sessions that they need to re-login.
 *
 * Triggered in two scenarios:
 * 1. App initialization with no Supabase session but stale MSAL cache present
 * 2. HTTP request interceptor detects no valid Supabase token but stale MSAL cache
 *
 * On user action ("Go to Login"):
 * - Clears all MSAL/Azure B2C session data
 * - Performs hard redirect to /login page
 */

import { Dialog } from "@win-reality/react-ui";
import { useEffect, useState } from "react";

import { SUPPORT_PHONE } from "@/lib/constants";
import { useMsalMigrationModal } from "@/stores/msal-migration-modal";
import { clearMsalSession } from "@/utils/msal-migration";

const MsalMigrationModal = () => {
  const { isOpen, close } = useMsalMigrationModal();
  const [isProcessing, setIsProcessing] = useState(false);

  // Reset processing state if modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsProcessing(false);
    }
  }, [isOpen]);

  const handleGoToLogin = async () => {
    setIsProcessing(true);

    try {
      // Clear all MSAL session data from storage
      clearMsalSession();

      // Perform hard redirect to login (fresh page load)
      window.location.href = "/login";
    } catch (error) {
      console.error("[MsalMigrationModal] Error clearing session:", error);
      // Even on error, redirect to login
      window.location.href = "/login";
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          close();
        }
      }}
      title="We've Upgraded Our Login System"
      defaultCancelButton={false}
      size="md"
      primaryAction={{
        label: "Continue to Login",
        onClick: handleGoToLogin,
        isLoading: isProcessing,
        isDisabled: isProcessing,
      }}
    >
      <div className="space-y-4 text-sm">
        <p className="text-gray-700">
          SmartParkOps has migrated to a central WIN login service to improve
          reliability and security. <br></br>
          <br></br>Your Account Manager has shared details on how to access
          using your new password.
        </p>

        <p className="text-gray-700">
          Connect with them at{" "}
          <a
            href={`tel:${SUPPORT_PHONE}`}
            className="font-medium text-blue-600 hover:underline"
          >
            {SUPPORT_PHONE}
          </a>
        </p>
      </div>
    </Dialog>
  );
};

export default MsalMigrationModal;
